import React, { useState, useEffect, useRef } from "react";
import { Modal } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import AddIcon from "@mui/icons-material/Add";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import SendIcon from "@mui/icons-material/Send";
import NotificationsOffOutlinedIcon from "@mui/icons-material/NotificationsOffOutlined";
import BlockIcon from "@mui/icons-material/Block";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import RemoveCircleIcon from "@mui/icons-material/RemoveCircle";
import Styles from "./ReasonModal.module.css";
import { ErrorMessage } from "../Toast/ErrorMessage";

const BASE_REASONS = {
  REJECTED: [
    "Incomplete documentation",
    "Verification failed",
    "Invalid company details",
    "Duplicate registration",
    "Non-compliance with regulations",
    "Failed background check",
    "Insufficient information provided",
  ],
  HOLD: [
    "Pending document verification",
    "Under compliance review",
    "Additional information required",
    "Regulatory hold",
    "Awaiting external verification",
  ],
  UN_VERIFIED: [
    "Company details changed",
    "Re-verification required",
    "Compliance review triggered",
    "Document re-submission needed",
    "Periodic re-verification",
  ],
};

const ACTION_CONFIG = {
  REJECTED: {
    label: "Reject Company",
    categoriesLabel: "Rejection reasons",
    confirmLabel: "Confirm Rejection",
    iconBg: "#fff8f9",
    iconBorder: "#ffd6da",
    iconColor: "#d50d21",
    btnBg: "#fff8f9",
    btnBorder: "#ff4c5e",
    btnColor: "#d50d21",
    Icon: BlockIcon,
    defaultEmailSubject: "Update on your company registration",
    defaultEmailMessage:
      "Thank you for registering with our platform. After careful review, we have decided not to proceed with your company registration at this stage. We appreciate your interest and encourage you to address the concerns raised before reapplying.",
  },
  HOLD: {
    label: "Hold Company",
    categoriesLabel: "Hold reasons",
    confirmLabel: "Confirm Hold",
    iconBg: "#fffdf4",
    iconBorder: "#ffe4a0",
    iconColor: "#c87d00",
    btnBg: "#fffdf4",
    btnBorder: "#f0a417",
    btnColor: "#c87d00",
    Icon: HourglassEmptyIcon,
    defaultEmailSubject: "Your company registration is on hold",
    defaultEmailMessage:
      "Your company registration has been placed on hold pending further review. Our team will reach out with next steps shortly. Thank you for your patience.",
  },
  UN_VERIFIED: {
    label: "Un-Verify Company",
    categoriesLabel: "Un-verification reasons",
    confirmLabel: "Confirm Un-Verify",
    iconBg: "#f3f3f4",
    iconBorder: "#e2e2e5",
    iconColor: "#525062",
    btnBg: "#f9f9f9",
    btnBorder: "#a9a8b1",
    btnColor: "#525062",
    Icon: RemoveCircleIcon,
    defaultEmailSubject: "Your company verification status has been updated",
    defaultEmailMessage:
      "Your company's verified status has been updated and requires re-verification. Please contact our team or resubmit the required documents to restore your verified status.",
  },
};

const today = () => new Date().toISOString().split("T")[0];

const ReasonModal = ({ open, onClose, actionType, onConfirm, companyName }) => {
  const config = ACTION_CONFIG[actionType] || ACTION_CONFIG.REJECTED;
  const ActionIcon = config.Icon;

  /* ── form state ── */
  const [actionDate, setActionDate] = useState("");
  const [reviewedBy, setReviewedBy] = useState("");
  const [opts, setOpts] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [notes, setNotes] = useState("");
  const [notify, setNotify] = useState("email");
  const [emailSubject, setEmailSubject] = useState("");
  const [emailMessage, setEmailMessage] = useState("");

  /* ── dropdown state ── */
  const [dropOpen, setDropOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [customInput, setCustomInput] = useState("");
  const dropRef = useRef(null);

  /* reset when modal opens / action type changes */
  useEffect(() => {
    if (open) {
      setActionDate(today());
      setReviewedBy("");
      setOpts([...(BASE_REASONS[actionType] || [])]);
      setSelected(new Set());
      setNotes("");
      setNotify("email");
      setEmailSubject(config.defaultEmailSubject);
      setEmailMessage(config.defaultEmailMessage);
      setDropOpen(false);
      setSearch("");
      setCustomInput("");
    }
  }, [open, actionType]); // eslint-disable-line

  /* click-outside closes dropdown */
  useEffect(() => {
    const handler = (e) => {
      if (dropRef.current && !dropRef.current.contains(e.target)) {
        setDropOpen(false);
        setSearch("");
      }
    };
    if (dropOpen) document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [dropOpen]);

  /* ── helpers ── */
  const toggleOption = (opt) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(opt) ? next.delete(opt) : next.add(opt);
      return next;
    });
  };

  const addCustom = () => {
    const v = customInput.trim();
    if (!v) return;
    if (!opts.includes(v)) setOpts((p) => [...p, v]);
    setSelected((p) => new Set([...p, v]));
    setCustomInput("");
  };

  const handleConfirm = () => {
    if (!actionDate) { ErrorMessage("Please select a date"); return; }
    if (selected.size === 0) { ErrorMessage("Please select at least one reason"); return; }
    if (notify === "email" && !emailSubject.trim()) { ErrorMessage("Please enter an email subject"); return; }

    onConfirm({
      date: actionDate,
      reviewedBy,
      reasons: Array.from(selected),
      notes,
      notify,
      emailSubject: notify === "email" ? emailSubject : "",
      emailMessage: notify === "email" ? emailMessage : "",
    });
    handleReset();
  };

  const handleReset = () => {
    setSelected(new Set());
    setNotes("");
    setDropOpen(false);
    setSearch("");
    setCustomInput("");
  };

  const handleClose = () => { handleReset(); onClose(); };

  const filteredOpts = opts.filter((o) =>
    o.toLowerCase().includes(search.toLowerCase())
  );
  const selectedArr = Array.from(selected);

  return (
    <Modal open={open} onClose={handleClose}>
      <div className={`popupModal ${Styles.modal}`}>

        {/* ── Header ── */}
        <div className={Styles.mhead}>
          <div className={Styles.mheadLeft}>
            <div
              className={Styles.iconWrap}
              style={{ background: config.iconBg, border: `1px solid ${config.iconBorder}` }}
            >
              <ActionIcon style={{ fontSize: "18px", color: config.iconColor }} />
            </div>
            <div>
              <div className={Styles.mheadTitle}>{config.label}</div>
              {companyName && (
                <div className={Styles.mheadSub}>{companyName}</div>
              )}
            </div>
          </div>
          <button className={Styles.closeBtn} onClick={handleClose} aria-label="Close">
            <CloseIcon style={{ fontSize: "15px" }} />
          </button>
        </div>

        {/* ── Scrollable body ── */}
        <div className={Styles.mbody}>

          {/* Row 1: date + reviewer */}
          <div className={Styles.twoCol}>
            <div className={Styles.field}>
              <label className={Styles.fieldLabel}>
                Action date <span className={Styles.req}>*</span>
              </label>
              <input
                type="date"
                className={Styles.fieldInput}
                value={actionDate}
                onChange={(e) => setActionDate(e.target.value)}
              />
            </div>
            <div className={Styles.field}>
              <label className={Styles.fieldLabel}>Reviewed by</label>
              <input
                type="text"
                className={Styles.fieldInput}
                placeholder="Reviewer name"
                value={reviewedBy}
                onChange={(e) => setReviewedBy(e.target.value)}
              />
            </div>
          </div>

          {/* Categories card */}
          <div className={Styles.sectionCard}>
            <div className={Styles.sectionCardHead}>
              <LocalOfferIcon style={{ fontSize: "15px", color: "var(--primary-color-text)" }} />
              <span className={Styles.sectionLabel}>
                {config.categoriesLabel} <span className={Styles.req}>*</span>
              </span>
              {selected.size > 0 && (
                <span className={Styles.catBadge}>{selected.size} selected</span>
              )}
            </div>
            <div className={Styles.sectionCardBody}>

              {/* Selected tags */}
              {selectedArr.length > 0 && (
                <div className={Styles.tagsArea}>
                  {selectedArr.map((t) => (
                    <span key={t} className={Styles.tag}>
                      {t}
                      <button
                        className={Styles.tagRemove}
                        onClick={() => toggleOption(t)}
                        aria-label={`Remove ${t}`}
                      >
                        <CloseIcon style={{ fontSize: "10px" }} />
                      </button>
                    </span>
                  ))}
                </div>
              )}

              {/* Dropdown */}
              <div ref={dropRef} className={Styles.dropWrap}>
                <button
                  className={`${Styles.dropTrigger} ${dropOpen ? Styles.dropTriggerOpen : ""}`}
                  onClick={() => { setDropOpen((p) => !p); setSearch(""); }}
                >
                  <span style={{ color: selectedArr.length ? "#333" : "#a9a8b1" }}>
                    {selectedArr.length
                      ? `${selectedArr.length} reason${selectedArr.length > 1 ? "s" : ""} selected`
                      : "Select one or more reasons…"}
                  </span>
                  <KeyboardArrowDownIcon
                    style={{
                      fontSize: "16px",
                      color: "#6f6d7c",
                      transform: dropOpen ? "rotate(180deg)" : "none",
                      transition: "transform 0.2s",
                    }}
                  />
                </button>

                {dropOpen && (
                  <div className={Styles.dropdown}>
                    <div className={Styles.dropSearch}>
                      <input
                        type="text"
                        placeholder="Search…"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        autoFocus
                      />
                    </div>
                    <div className={Styles.optList}>
                      {filteredOpts.map((o) => (
                        <div
                          key={o}
                          className={`${Styles.opt} ${selected.has(o) ? Styles.optSel : ""}`}
                          onClick={() => toggleOption(o)}
                        >
                          <input
                            type="checkbox"
                            checked={selected.has(o)}
                            onChange={() => toggleOption(o)}
                            onClick={(e) => e.stopPropagation()}
                          />
                          {o}
                        </div>
                      ))}
                      {filteredOpts.length === 0 && (
                        <div className={Styles.noOpts}>No results found</div>
                      )}
                    </div>
                    <div className={Styles.customRow}>
                      <input
                        type="text"
                        placeholder="Add custom reason…"
                        value={customInput}
                        onChange={(e) => setCustomInput(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && addCustom()}
                      />
                      <button className={Styles.addBtn} onClick={addCustom}>
                        <AddIcon style={{ fontSize: "12px" }} /> Add
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Internal notes card */}
          <div className={Styles.sectionCard}>
            <div className={Styles.sectionCardHead}>
              <ArticleOutlinedIcon style={{ fontSize: "15px", color: "var(--primary-color-text)" }} />
              <span className={Styles.sectionLabel}>Internal notes</span>
              <span className={Styles.notVisible}>Not visible to company</span>
            </div>
            <div className={Styles.sectionCardBody}>
              <textarea
                className={Styles.notesArea}
                rows={3}
                placeholder="Add context for your team — evaluation summary, feedback, key gaps…"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </div>
          </div>

          {/* Notification card */}
          <div className={Styles.sectionCard}>
            <div className={Styles.sectionCardHead}>
              <EmailOutlinedIcon style={{ fontSize: "15px", color: "var(--primary-color-text)" }} />
              <span className={Styles.sectionLabel}>Company notification</span>
            </div>
            <div className={`${Styles.sectionCardBody} ${Styles.notifyBody}`}>

              {/* Radio toggle */}
              <div className={Styles.radioGroup}>
                <label className={`${Styles.radioOpt} ${notify === "email" ? Styles.radioOptActive : ""}`}>
                  <input
                    type="radio"
                    name="rm-notify"
                    value="email"
                    checked={notify === "email"}
                    onChange={() => setNotify("email")}
                  />
                  <SendIcon style={{ fontSize: "13px" }} />
                  Send email
                </label>
                <label className={`${Styles.radioOpt} ${Styles.radioOptRight} ${notify === "none" ? Styles.radioOptActive : ""}`}>
                  <input
                    type="radio"
                    name="rm-notify"
                    value="none"
                    checked={notify === "none"}
                    onChange={() => setNotify("none")}
                  />
                  <NotificationsOffOutlinedIcon style={{ fontSize: "13px" }} />
                  Do not notify
                </label>
              </div>

              {/* Email fields */}
              {notify === "email" && (
                <div className={Styles.emailFields}>
                  <div className={Styles.field}>
                    <label className={Styles.fieldLabel}>Email subject</label>
                    <input
                      type="text"
                      className={Styles.fieldInput}
                      value={emailSubject}
                      onChange={(e) => setEmailSubject(e.target.value)}
                    />
                  </div>
                  <div className={Styles.field}>
                    <label className={Styles.fieldLabel}>Message</label>
                    <textarea
                      className={Styles.notesArea}
                      rows={3}
                      value={emailMessage}
                      onChange={(e) => setEmailMessage(e.target.value)}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

        </div>{/* end mbody */}

        {/* ── Footer ── */}
        <div className={Styles.mfoot}>
          <div className={Styles.footNote}>
            {selected.size > 0 ? (
              <>
                <span className={Styles.footCount}>{selected.size}</span>{" "}
                reason{selected.size > 1 ? "s" : ""} selected
              </>
            ) : (
              "Select at least one category to continue"
            )}
          </div>
          <div className={Styles.footBtns}>
            <button className={Styles.cancelBtn} onClick={handleClose}>
              Cancel
            </button>
            <button
              className={Styles.confirmBtn}
              onClick={handleConfirm}
              disabled={selected.size === 0}
              style={{
                background: config.btnBg,
                border: `1px solid ${config.btnBorder}`,
                color: config.btnColor,
              }}
            >
              {config.confirmLabel}
            </button>
          </div>
        </div>

      </div>
    </Modal>
  );
};

export default ReasonModal;
